import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Popper from '@mui/material/Popper';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { formatAMPMNeW, Images, renderClientName, renderStaffName, useSecondaryModal } from 'utils';
import { DRIVE, getDisplayFromFullType, isUnassignedAppt, SERVICE, UNAVAILABLE } from '../constants';
import { apptEnums } from './constants';
import { apptDetailStatus, UnassignedStaffChip } from './modals/apptDetail/constants';
import { ScheduleDetails } from './modals';

const QUICK_INFO_OPEN_DELAY_MS = 300;
const QUICK_INFO_CLOSE_DELAY_MS = 200;
const VIEWPORT_PADDING_PX = 8;

const priceFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "16:30" from an appt wall-clock ISO string → "4:30 PM" */
const wallClock = (iso) => (iso ? moment(iso.slice(11, 16), 'HH:mm').format('h:mm A') : '--');

/** "Davit P.": first name + last initial, as the calendar cards have always shown. */
const initialName = (person) =>
    person?.firstName ? `${person.firstName}${person.lastName ? ` ${person.lastName.slice(0, 1)}.` : ''}` : '';

const chargeRateLabel = (event) => {
    const modifiers = [event?.chargeRateModifier, event?.chargeRateModifier2, event?.chargeRateModifier3].filter(Boolean).join(', ');
    return [event?.chargeRateName, modifiers && `(${modifiers})`].filter(Boolean).join(' ');
};

const QuickInfoRow = ({ icon: Icon, label, children }) => (
    <div className="appt-quick-info-row">
        <Icon className="appt-quick-info-icon" aria-hidden />
        <span className="appt-quick-info-label">{label}</span>
        <div className="appt-quick-info-value">{children}</div>
    </div>
);

/** Hover / focus card for a calendar appt. Only reads the calendar list payload (ApptBasicInfoDTO). */
const ApptQuickInfo = ({ event, onOpenDetails }) => {
    const isService = event?.eventType === SERVICE;
    const title = isService ? event?.serviceName || getDisplayFromFullType(event?.eventType) : event?.subject || getDisplayFromFullType(event?.eventType);
    const location = event?.locationLabel || event?.location?.formattedAddress;
    const rateLabel = isService ? chargeRateLabel(event) : '';

    return (
        <div className="appt-quick-info" role="dialog" aria-label={`${title} details`}>
            <div className="appt-quick-info-header">
                <div className="appt-quick-info-title-row">
                    <p className="appt-quick-info-title">{title}</p>
                    {apptDetailStatus(event?.status, event)}
                </div>
                {isService && (event?.cptCode || rateLabel) && (
                    <div className="appt-quick-info-billing">
                        {event?.cptCode && <span className="appt-quick-info-cpt">{event.cptCode}</span>}
                        {rateLabel && <span>{rateLabel}</span>}
                    </div>
                )}
            </div>

            <QuickInfoRow icon={AccessTimeIcon} label="When">
                {`${moment.utc(event?.startDate).format('ddd, MMM D, YYYY')} · ${wallClock(event?.originalStart)} – ${wallClock(event?.originalEnd)}`}
            </QuickInfoRow>
            {(location || event?.posName) && (
                <QuickInfoRow icon={PlaceOutlinedIcon} label="Where">
                    {location && <p>{location}</p>}
                    {event?.posName && <p className="appt-quick-info-muted">{event.posName}</p>}
                </QuickInfoRow>
            )}
            {event?.client && (
                <QuickInfoRow icon={PersonOutlineIcon} label="Client">
                    {renderClientName(event.client)}
                </QuickInfoRow>
            )}
            <QuickInfoRow icon={BadgeOutlinedIcon} label="Staff">
                {isUnassignedAppt(event) ? <UnassignedStaffChip size="sm" /> : renderStaffName(event?.staff)}
            </QuickInfoRow>
            {(event?.supervision || event?.supervisee) && (
                <QuickInfoRow icon={SupervisorAccountOutlinedIcon} label="Supervision">
                    {event?.supervisee ? `Supervising ${renderStaffName(event.supervisee)}` : 'Supervision'}
                </QuickInfoRow>
            )}
            {event?.isActualTimeSet && event?.scheduleDateTime && (
                <QuickInfoRow icon={HistoryIcon} label="Actual time">
                    <p>{`${wallClock(event?.originalStart)} – ${wallClock(event?.originalEnd)}`}</p>
                    <p className="appt-quick-info-muted">{`Scheduled: ${event.scheduleDateTime}`}</p>
                </QuickInfoRow>
            )}
            {event?.status === apptEnums.CANCELLED && event?.cancelReason && (
                <p className="appt-quick-info-muted">{`Cancel reason: ${event.cancelReason}`}</p>
            )}

            <div className="appt-quick-info-footer">
                <button type="button" className="appt-quick-info-link" onClick={onOpenDetails}>
                    Appointment Details
                </button>
            </div>
        </div>
    );
};

/**
 * Calendar card: provider, client and time, one ellipsized line each (rows that don't fit the card's height are
 * clipped). Hovering or keyboard-focusing the card opens ApptQuickInfo; mouse leave, Esc, blur or a mousedown
 * (click / drag start) closes it.
 */
export const CalendarEvent = ({ event }) => {
    const ref = useRef(null);
    const popperRef = useRef(null);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const [anchor, setAnchor] = useState(null);
    const { openSecondary } = useSecondaryModal();

    const clearTimers = () => {
        clearTimeout(openTimer.current);
        clearTimeout(closeTimer.current);
    };
    const scheduleClose = useCallback(() => {
        clearTimeout(openTimer.current);
        closeTimer.current = setTimeout(() => setAnchor(null), QUICK_INFO_CLOSE_DELAY_MS);
    }, []);
    const closeNow = useCallback(() => {
        clearTimers();
        setAnchor(null);
    }, []);

    useEffect(() => {
        // The focusable, hoverable box is react-big-calendar's .rbc-event wrapper, not this component.
        const host = ref.current?.closest('.rbc-event');
        if (!host) return undefined;
        const open = (delay) => () => {
            clearTimers();
            openTimer.current = setTimeout(() => setAnchor(host), delay);
        };
        const onHover = open(QUICK_INFO_OPEN_DELAY_MS);
        const onFocus = (e) => e.target === host && open(0)();
        const onKeyDown = (e) => e.key === 'Escape' && closeNow();
        host.addEventListener('mouseenter', onHover);
        host.addEventListener('mouseleave', scheduleClose);
        host.addEventListener('focus', onFocus);
        host.addEventListener('blur', scheduleClose);
        host.addEventListener('mousedown', closeNow);
        host.addEventListener('keydown', onKeyDown);
        return () => {
            clearTimers();
            host.removeEventListener('mouseenter', onHover);
            host.removeEventListener('mouseleave', scheduleClose);
            host.removeEventListener('focus', onFocus);
            host.removeEventListener('blur', scheduleClose);
            host.removeEventListener('mousedown', closeNow);
            host.removeEventListener('keydown', onKeyDown);
        };
    }, [scheduleClose, closeNow]);

    // While open, the card stays up as long as the pointer is over the calendar card or the quick info itself
    // (the Popper is portaled to <body>, so it isn't inside the card's DOM). Esc also closes a hover-opened card.
    useEffect(() => {
        if (!anchor) return undefined;
        const onKeyDown = (e) => e.key === 'Escape' && closeNow();
        const onMouseOver = (e) => {
            const isInside = anchor.contains(e.target) || popperRef.current?.contains(e.target);
            clearTimeout(closeTimer.current);
            if (!isInside) scheduleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mouseover', onMouseOver);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mouseover', onMouseOver);
        };
    }, [anchor, closeNow, scheduleClose]);

    const handleOpenDetails = () => {
        closeNow();
        openSecondary(<ScheduleDetails currentId={event?.id} />);
    };

    const isService = event?.eventType === SERVICE;
    const isCompleted = event?.status === apptEnums.COMPLETED;
    const isCancelled = event?.status === apptEnums.CANCELLED;
    const StatusIcon = isCompleted ? CheckIcon : isCancelled ? CloseIcon : null;
    const staffName = initialName(event?.staff);
    const clientName = initialName(event?.client);
    const title = [staffName, clientName].filter(Boolean).join(' - ');

    return (
        <div ref={ref} className="calendar-card-staff" style={{ color: event?.textColor }}>
            <div className="calendar-card-line">
                {StatusIcon && <StatusIcon className="calendar-card-status-icon" />}
                {isUnassignedAppt(event) && <UnassignedStaffChip size="sm" />}
                <p className="calendar-card-text" style={isCancelled ? { textDecoration: 'line-through' } : undefined}>
                    {title}
                    {!isService && event?.subject ? ` (${event.subject})` : ''}
                </p>
                <span className="calendar-card-icons">
                    {event?.eventType === DRIVE && <img src={Images.truck} alt="Drive" />}
                    {event?.eventType === UNAVAILABLE && <img src={Images.unavailable} alt="Unavailable" />}
                    {event?.supervisee && <img src={Images.supervizeEye} alt="Supervision" />}
                    {event?.isActualTimeSet && event?.scheduleDateTime && (
                        <img className="calendar-card-actual-icon" src={Images.actualTimeWhite} alt="Actual time set" />
                    )}
                </span>
            </div>
            <p className="calendar-card-text calendar-card-time">
                {isService && event?.serviceCptCode ? `${event.serviceCptCode} ` : ''}
                {`[${formatAMPMNeW(event?.originalStart)}-${formatAMPMNeW(event?.originalEnd)}]`}
            </p>

            <Popper
                ref={popperRef}
                open={!!anchor}
                anchorEl={anchor}
                placement="bottom-start"
                className="appt-quick-info-popper"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 0] } },
                    { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', altAxis: true, padding: VIEWPORT_PADDING_PX } },
                ]}
                // The Popper is portaled to <body>, but React still bubbles its events to the .rbc-event
                // (whose onClick opens the detail modal) — stop them here.
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
            >
                <ApptQuickInfo event={event} onOpenDetails={handleOpenDetails} />
            </Popper>
        </div>
    );
};
